import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Plus, RefreshCw, Send, Wallet, X, Check, ArrowUp, ArrowDown, RotateCw } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const Portal = ({ children }) => createPortal(children, document.body);

export default function PayoutsAdminPage() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const [wallets, setWallets] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [vendors, setVendors] = useState([]);     // registry: [{id,label}]
    const [vendorBal, setVendorBal] = useState(null);
    const [err, setErr] = useState("");
    const [top, setTop] = useState({ partner_id: "", amount: "", note: "" });
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);   // partner_id being edited (vendor allowlist)
    const [editSel, setEditSel] = useState([]);     // currently selected vendor ids
    const [editBusy, setEditBusy] = useState(false);
    const [midEditing, setMidEditing] = useState(null); // partner_id for MID assignment
    const [midSel, setMidSel] = useState([]);       // ordered mid_ids
    const [allMids, setAllMids] = useState([]);
    const [midBusy, setMidBusy] = useState(false);
    const [resending, setResending] = useState(null);   // payout_id currently resending
    const [resendMsg, setResendMsg] = useState(null);   // {payout_id, text, kind}
    const [ipEditing, setIpEditing] = useState(null);   // partner_id for IP whitelist
    const [ipPortal, setIpPortal] = useState("");       // comma/newline-separated
    const [ipApi, setIpApi] = useState("");
    const [ipBusy, setIpBusy] = useState(false);

    const refresh = async () => {
        setErr("");
        try {
            const [w, p] = await Promise.all([
                api.get("/admin/payouts/wallets"),
                api.get("/admin/payouts/overview"),
            ]);
            setWallets(w.data.items || []);
            setPayouts(p.data.items || []);
            // Vendor + MID lists are super-admin-only; skip if the current
            // user is a regular admin to avoid noisy 403s in the UI.
            if (isSuper) {
                try {
                    const [v, m] = await Promise.all([
                        api.get("/admin/payouts/vendors"),
                        api.get("/admin/payouts/mids"),
                    ]);
                    setVendors(v.data.items || []);
                    setAllMids(m.data.items || []);
                } catch {}
            }
            try { const vb = await api.get("/admin/payouts/vendor-balance"); setVendorBal(vb.data); } catch {}
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    useEffect(() => { refresh(); }, []);

    const topup = async () => {
        if (!top.partner_id || !top.amount) return;
        setBusy(true); setErr("");
        try {
            await api.post("/admin/payouts/topup", {
                partner_id: top.partner_id.trim(),
                amount: Number(top.amount),
                note: top.note || null,
            });
            setTop({ partner_id: "", amount: "", note: "" });
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const openEdit = (w) => {
        setEditing(w.partner_id);
        setEditSel(w.allowed_vendors || []);
    };

    const toggleVendor = (vid) => {
        setEditSel((s) => s.includes(vid) ? s.filter((x) => x !== vid) : [...s, vid]);
    };

    const saveVendors = async () => {
        if (!editing) return;
        setEditBusy(true); setErr("");
        try {
            await api.patch(`/admin/payouts/wallet/${editing}/vendors`, {
                allowed_vendors: editSel,
            });
            setEditing(null); setEditSel([]);
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setEditBusy(false); }
    };

    const openMidEdit = (w) => {
        setMidEditing(w.partner_id);
        setMidSel(w.assigned_mids || []);
    };
    const toggleMid = (mid_id) => {
        setMidSel((s) => s.includes(mid_id) ? s.filter((x) => x !== mid_id) : [...s, mid_id]);
    };
    const moveMid = (mid_id, direction) => {
        const idx = midSel.indexOf(mid_id);
        if (idx === -1) return;
        const next = [...midSel];
        const swap = direction === "up" ? idx - 1 : idx + 1;
        if (swap < 0 || swap >= next.length) return;
        [next[idx], next[swap]] = [next[swap], next[idx]];
        setMidSel(next);
    };
    const saveMids = async () => {
        if (!midEditing) return;
        setMidBusy(true); setErr("");
        try {
            await api.patch(`/admin/payouts/wallet/${midEditing}/mids`, {
                assigned_mids: midSel,
            });
            setMidEditing(null); setMidSel([]);
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setMidBusy(false); }
    };

    const openIpEdit = async (w) => {
        setIpEditing(w.partner_id);
        try {
            const r = await api.get(`/admin/payouts/wallet/${w.partner_id}/ip-whitelist`);
            setIpPortal((r.data.portal_ip_whitelist || []).join("\n"));
            setIpApi((r.data.api_ip_whitelist || []).join("\n"));
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    const saveIpWhitelist = async () => {
        if (!ipEditing) return;
        setIpBusy(true); setErr("");
        try {
            await api.patch(`/admin/payouts/wallet/${ipEditing}/ip-whitelist`, {
                portal_ip_whitelist: ipPortal.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
                api_ip_whitelist: ipApi.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
            });
            setIpEditing(null); setIpPortal(""); setIpApi("");
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setIpBusy(false); }
    };

    const vendorLabel = (vid) => (vendors.find((v) => v.id === vid)?.label) || vid;

    const resendWebhook = async (payoutId) => {
        setResending(payoutId); setResendMsg(null); setErr("");
        try {
            const r = await api.post(`/admin/payouts/${payoutId}/resend-webhook`);
            const sc = r.data.status_code;
            setResendMsg({
                payout_id: payoutId,
                text: sc && sc < 300 ? `Resent — partner responded ${sc}` : (r.data.error || `Partner responded ${sc || "?"} — check endpoint`),
                kind: sc && sc < 300 ? "ok" : "warn",
            });
            // Auto-clear after 5s
            setTimeout(() => setResendMsg((m) => (m && m.payout_id === payoutId ? null : m)), 5000);
        } catch (e) {
            setResendMsg({
                payout_id: payoutId,
                text: formatDetail(e?.response?.data?.detail) || e.message,
                kind: "err",
            });
            setTimeout(() => setResendMsg((m) => (m && m.payout_id === payoutId ? null : m)), 5000);
        } finally { setResending(null); }
    };

    const canResend = (p) => ["SUCCESS", "FAILED", "REVERSED"].includes(p.status);

    return (
        <div data-testid="admin-payouts-page" className="space-y-6 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Send className="w-6 h-6 text-blue-600" /> Payouts (Admin)
                    </h1>
                    <p className="text-xs text-slate-500 mt-1">Top up partner wallets, control which vendors each partner can use.</p>
                </div>
                <button onClick={refresh} data-testid="admin-payouts-refresh"
                        className="btn-secondary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>

            {vendorBal && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
                    <div className="flex items-center gap-2 text-amber-800 font-semibold mb-1">
                        <Wallet className="w-3.5 h-3.5" /> Upstream vendor wallet ({vendorBal.vendor})
                    </div>
                    <div className="font-mono text-amber-900" data-testid="admin-vendor-balance">
                        {JSON.stringify(vendorBal.raw, null, 2).slice(0, 200)}
                    </div>
                </div>
            )}

            {err && <div data-testid="admin-payouts-error" className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            {/* Top-up form */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h2 className="font-semibold mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> Credit partner wallet</h2>
                <div className="grid grid-cols-4 gap-2">
                    <input data-testid="topup-partner-id" placeholder="partner_id" value={top.partner_id}
                            onChange={(e) => setTop({ ...top, partner_id: e.target.value })} className="input text-xs" />
                    <input data-testid="topup-amount" type="number" placeholder="Amount ₹" value={top.amount}
                            onChange={(e) => setTop({ ...top, amount: e.target.value })} className="input text-xs" />
                    <input data-testid="topup-note" placeholder="Note (optional)" value={top.note}
                            onChange={(e) => setTop({ ...top, note: e.target.value })} className="input text-xs" />
                    <button data-testid="topup-submit" onClick={topup} disabled={busy}
                            className="btn-primary px-3 py-2 rounded-md text-xs flex items-center justify-center gap-2">
                        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Credit
                    </button>
                </div>
            </div>

            {/* Wallets table */}
            <div>
                <h2 className="font-semibold mb-2 text-sm">Partner wallets ({wallets.length})</h2>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                            <tr>
                                <th className="px-3 py-2 text-left">Partner</th>
                                <th className="px-3 py-2 text-right">Balance</th>
                                <th className="px-3 py-2 text-left">Vendors</th>
                                <th className="px-3 py-2 text-left">MIDs</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {wallets.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No wallets.</td></tr>}
                            {wallets.map((w) => {
                                const av = w.allowed_vendors || [];
                                const assigned = w.assigned_mids || [];
                                return (
                                    <tr key={w.partner_id} data-testid={`wallet-row-${w.partner_id}`}
                                        className={`border-t border-slate-100 ${!w.wallet_initialised ? "bg-slate-50/50" : ""}`}>
                                        <td className="px-3 py-2">
                                            <div className="font-semibold" data-testid={`wallet-biz-${w.partner_id}`}>
                                                {w.business_name || w.partner_id}
                                                {!w.wallet_initialised && (
                                                    <span className="ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-100 text-amber-800" title="Partner has not initiated any payout yet — wallet auto-creates on first use.">NOT ONBOARDED</span>
                                                )}
                                            </div>
                                            <div className="text-[10px] text-slate-500 font-mono">{w.partner_id}{w.email ? ` · ${w.email}` : ""}</div>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono font-semibold">{fmtINR(w.balance)}</td>
                                        <td className="px-3 py-2">
                                            {av.length === 0 ? (
                                                <span className="text-rose-600 italic text-[11px]" data-testid={`wallet-vendors-${w.partner_id}`}>
                                                    none
                                                </span>
                                            ) : (
                                                <div className="flex flex-wrap gap-1" data-testid={`wallet-vendors-${w.partner_id}`}>
                                                    {av.map((vid) => (
                                                        <span key={vid} className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
                                                            {vendorLabel(vid)}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            {assigned.length === 0 ? (
                                                <span className="text-rose-600 italic text-[11px]" data-testid={`wallet-mids-${w.partner_id}`}>
                                                    none — payouts disabled
                                                </span>
                                            ) : (
                                                <div className="flex flex-wrap gap-1" data-testid={`wallet-mids-${w.partner_id}`}>
                                                    {assigned.map((mid_id, i) => {
                                                        const m = allMids.find((x) => x.mid_id === mid_id);
                                                        return (
                                                            <span key={mid_id}
                                                                className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                                                    i === 0 ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-700"
                                                                } ${m?.status === "paused" ? "opacity-50 line-through" : ""}`}>
                                                                {i === 0 ? "★ " : ""}{m?.label || mid_id.slice(0, 8)}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right space-x-3">
                                            {isSuper ? (
                                                <>
                                                    <button onClick={() => openEdit(w)}
                                                            data-testid={`edit-vendors-${w.partner_id}`}
                                                            className="text-blue-600 hover:text-blue-800 text-xs underline">
                                                        Vendors
                                                    </button>
                                                    <button onClick={() => openMidEdit(w)}
                                                            data-testid={`edit-mids-${w.partner_id}`}
                                                            className="text-indigo-600 hover:text-indigo-800 text-xs underline font-semibold">
                                                        MIDs
                                                    </button>
                                                    <button onClick={() => openIpEdit(w)}
                                                            data-testid={`edit-ip-${w.partner_id}`}
                                                            className="text-fuchsia-600 hover:text-fuchsia-800 text-xs underline">
                                                        IP WL
                                                    </button>
                                                </>
                                            ) : (
                                                <span data-testid={`edit-locked-${w.partner_id}`}
                                                      className="text-[10px] text-slate-400 italic">super admin only</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* All payouts */}
            <div>
                <h2 className="font-semibold mb-2 text-sm">All payouts ({payouts.length})</h2>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                            <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Partner</th><th className="px-3 py-2 text-left">Payout</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-left">Vendor</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2"></th></tr>
                        </thead>
                        <tbody>
                            {payouts.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No payouts yet.</td></tr>}
                            {payouts.map((p) => (
                                <tr key={p.payout_id} className="border-t border-slate-100">
                                    <td className="px-3 py-2 text-slate-500 text-[11px]">{new Date(p.created_at).toLocaleString()}</td>
                                    <td className="px-3 py-2 font-mono text-[11px]">{p.partner_id}</td>
                                    <td className="px-3 py-2 font-mono">{p.payout_id}</td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtINR(p.amount)}</td>
                                    <td className="px-3 py-2 text-slate-500">{p.vendor}<div className="text-[10px] font-mono text-slate-400">{p.vendor_order_id}</div></td>
                                    <td className="px-3 py-2">
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700">{p.status}</span>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {canResend(p) ? (
                                            <button onClick={() => resendWebhook(p.payout_id)}
                                                    disabled={resending === p.payout_id}
                                                    data-testid={`resend-webhook-${p.payout_id}`}
                                                    className="text-indigo-600 hover:text-indigo-800 text-xs inline-flex items-center gap-1 disabled:opacity-40">
                                                {resending === p.payout_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                                                Resend webhook
                                            </button>
                                        ) : (
                                            <span className="text-[10px] text-slate-400 italic">in-flight</span>
                                        )}
                                        {resendMsg?.payout_id === p.payout_id && (
                                            <div data-testid={`resend-result-${p.payout_id}`}
                                                className={`mt-1 text-[10px] ${
                                                    resendMsg.kind === "ok" ? "text-emerald-700" :
                                                    resendMsg.kind === "warn" ? "text-amber-700" : "text-rose-700"
                                                }`}>
                                                {resendMsg.text}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit vendors modal */}
            {editing && (<Portal>
                <div data-testid="edit-vendors-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-xl p-6 w-[460px] shadow-xl">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <h3 className="font-bold text-lg">Allowed vendors</h3>
                                <p className="text-xs text-slate-500 mt-0.5 font-mono">{editing}</p>
                            </div>
                            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="text-xs text-slate-600 mb-3">
                            Pick which payout vendors this partner can dispatch to. First selected is the default when their API request omits <code className="font-mono bg-slate-100 px-1 rounded">vendor</code>. Empty = payouts disabled.
                        </p>
                        <div className="space-y-2 mb-4">
                            {vendors.length === 0 && <p className="text-xs text-slate-400 italic">No vendors registered on the server.</p>}
                            {vendors.map((v) => {
                                const checked = editSel.includes(v.id);
                                return (
                                    <label key={v.id} data-testid={`vendor-opt-${v.id}`}
                                        className={`flex items-center gap-3 p-2.5 border rounded cursor-pointer transition ${
                                            checked ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
                                        }`}>
                                        <input type="checkbox" checked={checked} onChange={() => toggleVendor(v.id)}
                                               data-testid={`vendor-chk-${v.id}`}
                                               className="w-4 h-4 accent-blue-600" />
                                        <div className="flex-1">
                                            <div className="font-semibold text-sm">{v.label}</div>
                                            <div className="text-[11px] text-slate-500 font-mono">{v.id}</div>
                                        </div>
                                        {checked && <Check className="w-4 h-4 text-blue-600" />}
                                    </label>
                                );
                            })}
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setEditing(null)}
                                    data-testid="edit-vendors-cancel"
                                    className="btn-secondary px-3 py-2 rounded-md text-xs">Cancel</button>
                            <button onClick={saveVendors} disabled={editBusy}
                                    data-testid="edit-vendors-save"
                                    className="btn-primary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                                {editBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
                            </button>
                        </div>
                    </div>
                </div>
            </Portal>)}
            {/* Edit MIDs modal */}
            {midEditing && (<Portal>
                <div data-testid="edit-mids-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-xl p-6 w-[540px] max-h-[85vh] overflow-y-auto shadow-xl">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <h3 className="font-bold text-lg">Assigned MIDs</h3>
                                <p className="text-xs text-slate-500 mt-0.5 font-mono">{midEditing}</p>
                            </div>
                            <button onClick={() => setMidEditing(null)} className="text-slate-400 hover:text-slate-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="text-xs text-slate-600 mb-3">
                            Pick MIDs to assign. Order matters — the ★ first active MID is the dispatch default. Partners can override via <code className="font-mono bg-slate-100 px-1 rounded">mid</code> in API body.
                        </p>

                        {/* Selected (ordered) */}
                        {midSel.length > 0 && (
                            <div className="mb-3">
                                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Priority order (first = default)</div>
                                <div className="space-y-1">
                                    {midSel.map((mid_id, i) => {
                                        const m = allMids.find((x) => x.mid_id === mid_id);
                                        if (!m) return null;
                                        return (
                                            <div key={mid_id} data-testid={`mid-sel-${mid_id}`}
                                                className="flex items-center gap-2 p-2 border border-indigo-200 bg-indigo-50 rounded">
                                                <span className="text-[10px] font-mono text-indigo-500 w-5">{i + 1}</span>
                                                <div className="flex-1">
                                                    <div className="font-semibold text-xs">{m.label}</div>
                                                    <div className="text-[10px] text-slate-500">{m.vendor} · {m.status}</div>
                                                </div>
                                                <button onClick={() => moveMid(mid_id, "up")}
                                                        disabled={i === 0}
                                                        data-testid={`mid-up-${mid_id}`}
                                                        className="p-1 rounded hover:bg-white disabled:opacity-30">
                                                    <ArrowUp className="w-3 h-3" />
                                                </button>
                                                <button onClick={() => moveMid(mid_id, "down")}
                                                        disabled={i === midSel.length - 1}
                                                        data-testid={`mid-down-${mid_id}`}
                                                        className="p-1 rounded hover:bg-white disabled:opacity-30">
                                                    <ArrowDown className="w-3 h-3" />
                                                </button>
                                                <button onClick={() => toggleMid(mid_id)}
                                                        data-testid={`mid-remove-${mid_id}`}
                                                        className="text-rose-600 hover:text-rose-800">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Available (unselected) */}
                        <div>
                            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Available MIDs</div>
                            <div className="space-y-1 max-h-[200px] overflow-y-auto">
                                {allMids.filter((m) => !midSel.includes(m.mid_id)).length === 0 && (
                                    <p className="text-xs text-slate-400 italic p-2">All MIDs are selected. Create more under "Vendors & MIDs".</p>
                                )}
                                {allMids.filter((m) => !midSel.includes(m.mid_id)).map((m) => (
                                    <button key={m.mid_id} onClick={() => toggleMid(m.mid_id)}
                                            data-testid={`mid-add-${m.mid_id}`}
                                            className="w-full flex items-center gap-2 p-2 border border-slate-200 rounded hover:border-indigo-300 hover:bg-indigo-50 transition text-left">
                                        <Plus className="w-3.5 h-3.5 text-indigo-600" />
                                        <div className="flex-1">
                                            <div className="font-semibold text-xs">{m.label}</div>
                                            <div className="text-[10px] text-slate-500">{m.vendor} · {m.status}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={() => setMidEditing(null)}
                                    data-testid="edit-mids-cancel"
                                    className="btn-secondary px-3 py-2 rounded-md text-xs">Cancel</button>
                            <button onClick={saveMids} disabled={midBusy}
                                    data-testid="edit-mids-save"
                                    className="btn-primary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                                {midBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
                            </button>
                        </div>
                    </div>
                </div>
            </Portal>)}

            {/* Edit IP whitelist modal */}
            {ipEditing && (<Portal>
                <div className="fixed inset-0 bg-black/40 z-[100] flex items-start sm:items-center justify-center p-4 overflow-y-auto">
                    <div data-testid="edit-ip-modal" className="bg-white rounded-xl w-full max-w-lg p-5 space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-bold text-base">IP Whitelist</h3>
                                <p className="text-[11px] text-slate-500 font-mono">{ipEditing}</p>
                            </div>
                            <button onClick={() => setIpEditing(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                        </div>
                        <p className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded p-2.5">
                            One IP or CIDR per line (e.g. <code>203.0.113.5</code> or <code>10.0.0.0/24</code>). Empty = no restriction (allow all).
                        </p>
                        <div>
                            <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Portal login IPs</label>
                            <textarea data-testid="ip-portal" value={ipPortal} rows={4}
                                        onChange={(e) => setIpPortal(e.target.value)}
                                        placeholder="203.0.113.5&#10;10.0.0.0/24"
                                        className="input w-full text-sm font-mono mt-1" />
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">API call IPs</label>
                            <textarea data-testid="ip-api" value={ipApi} rows={4}
                                        onChange={(e) => setIpApi(e.target.value)}
                                        placeholder="35.184.53.215&#10;198.51.100.0/26"
                                        className="input w-full text-sm font-mono mt-1" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setIpEditing(null)}
                                    className="btn-secondary px-3 py-2 rounded-md text-xs">Cancel</button>
                            <button onClick={saveIpWhitelist} disabled={ipBusy}
                                    data-testid="ip-save"
                                    className="btn-primary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                                {ipBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save whitelist
                            </button>
                        </div>
                    </div>
                </div>
            </Portal>)}
        </div>
    );
}
